import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Reorder } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useSession } from '@/features/auth/hooks/useSession';
import { useToast } from '@/app/providers/ToastContext';
import { confirmDialog } from '@/common/components/ConfirmDialog';
import { useConversation } from '@/app/providers/ConversationContext';
import { useModulePane } from '@/app/providers/ModulePaneContext';
import { api } from '@/lib/api';
import type { WorkspaceModule, ModuleSidebarPreference } from '@/lib/api/endpoints/workspaces';
import { POLL_INTERVAL } from '@/modules/channels/constants';
import { getSpaceMembers, type SpaceMember } from '@/lib/api/endpoints/spaces';
import { useNotifications as useNotificationsContext } from '@/features/notifications/useNotifications';
import { queryKeys } from '@/lib/query/keys';
import { prefetchModuleChunk, prefetchModuleData } from '@/lib/prefetch-module';
import { buildConversationPath, resolveWorkspaceId } from '@/lib/workspace-routing';
import { useIsModuleTypeHidden } from '@/features/entitlements/hooks/useEntitlements';
import { useChatDock } from '@/app/providers/ChatDockContext';
import { conversationEvents } from '@/lib/conversation-events';
import { ModuleFlyout } from './ModuleFlyout';
import { PlanChoiceModal } from '@/features/billing/components/PlanChoiceModal';
import {
  RAIL_MODULE_TYPES,
  DEFAULT_VISIBILITY,
  CHECKLIST_MODULE_TYPES,
  ALL_CORE_MODULE_TYPES,
  RAIL_LABELS,
  PERSONAL_RAIL_LABELS,
  RAIL_PLUGIN_IDS,
  railKeyToVisibilityKey,
  PROVIDER_RAIL_KEYS,
  DEFAULT_RAIL_ORDER,
  TEAM_RAIL_ORDER,
  LEAF_GROUP_RAIL_KEYS,
} from './SidebarRail.constants';
import { getPrimaryModuleIcon, PLUGIN_MODULE_META, FALLBACK_PLUGIN_ICON, RAIL_ITEM_ICONS } from './SidebarRail.icons';
import {
  isPrimaryRailModule,
  sortRailItemsByOrder,
  aliasLegacyRailVisibility,
  aliasLegacyRailOrder,
  mergeRailOrder,
  dmUnreadByUserId,
  computeOffManageKeys,
  visibilityKeyToRailKey,
  railModulesScrollClassName,
  isIndividualTeamRailKey,
  isSharedTeamRailKey,
} from './SidebarRail.utils';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { getContainerKey } from './sidebar/utils';
import { RailItem } from './SidebarRail.RailItem';
import { ChatHistoryFlyout } from './SidebarRail.ChatHistoryFlyout';
import { useNotifications } from '@/modules/automations/redesign/useAutomationsUi';
import { automationsRailHasUnread } from './automationsRailBadge';
import { useRailAutoCollapse, initialRailExpansion } from './SidebarRail.autoCollapse';
import './SidebarRail.css';

// Re-exported so existing consumers (e.g. SidebarRail.test.ts) keep importing it from './SidebarRail'.
export { calculateToggleAnimDistance } from './SidebarRail.utils';


interface SidebarRailProps {
  onNewChat: () => void;
  onOpenSettings?: () => void;
  onOpenIntegrations?: () => void;
  onOpenWorkspaceOptions?: () => void;
}

export function SidebarRail({ onNewChat, onOpenSettings, onOpenIntegrations, onOpenWorkspaceOptions }: SidebarRailProps) {
  // Saved preference clamped by viewport — mounting inside the narrow band
  // paints icon-only directly (no expand→collapse flash on load).
  const [expanded, setExpanded] = useState(initialRailExpansion);
  // Kill the squeezed desktop band: below BREAKPOINTS.railExpandedMin the
  // expanded rail no longer leaves room for the chat's full 680px lane, so
  // force the icon-only rail (the chat keeps 680px down to the 768px mobile
  // cut). Acts only on boundary crossings — a manual expand in the narrow band
  // is honored until the next crossing; widening restores the saved
  // preference. See SidebarRail.autoCollapse.
  useRailAutoCollapse(setExpanded);
  const [openFlyout, setOpenFlyout] = useState<string | null>(null);
  // 'individual' = top sliders (email/calendar/drive); 'team' = bottom sliders (team-only); null = closed
  const [managerSection, setManagerSection] = useState<'individual' | 'team' | null>(null);
  const isManagerOpen = managerSection !== null;
  // Module reorder lives INSIDE manager ("Module settings") mode: while it's
  // open the category items become draggable (Framer Reorder — smooth + works
  // on touch) and show a 3-dot grip on the left. `tempRailOrder` holds the live
  // drag order; the committed order persists per workspace in the
  // sidebar-settings module config (see persistRailOrder).
  const [tempRailOrder, setTempRailOrder] = useState<string[] | null>(null);
  const pendingRailOrderRef = useRef<string[] | null>(null);
  // Current on-list display order (visible core rail keys), mirrored into a ref
  // each render so the toggle handler — defined above where it's computed — can
  // append a re-enabled module to the true bottom of the on list.
  const orderedRailKeysRef = useRef<string[]>([]);
  // Off-group recency stamp (session-local): a just-disabled module rises to the
  // TOP of the off group. The ON group is deliberately NOT re-sorted — it keeps
  // the persisted rail order so the modules read in the exact same order while
  // editing as they do all the time. Higher number = more recent.
  const stampSeqRef = useRef(0);
  const [offStamp, setOffStamp] = useState<Record<string, number>>({});
  // Optimistic visibility overlay keyed by visibility key — a toggle applies
  // here instantly (the row dims/un-dims and slides to its boundary on the next
  // frame) instead of waiting ~300ms for the PATCH + refetch. Each key is
  // dropped once the refetched server value matches it (effect below).
  const [optimisticVis, setOptimisticVis] = useState<Record<string, boolean>>({});
  // The last enabled module can't be turned off (a workspace needs at least one);
  // attempting it shakes the row instead. Holds that row's rail key briefly.
  const [shakeKey, setShakeKey] = useState<string | null>(null);
  const shakeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current); }, []);
  // Drag is constrained to this container so an item can't be dragged past the
  // top/bottom of the modules list.
  const railReorderGroupRef = useRef<HTMLDivElement>(null);
  // The scrollable modules list — used to smooth-scroll back to the top when
  // leaving manage mode (so the list slides up instead of teleporting).
  const railScrollRef = useRef<HTMLDivElement>(null);
  const managerCloseRafRef = useRef<number | null>(null);
  const managerCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelManagerClose = () => {
    if (managerCloseRafRef.current != null) { cancelAnimationFrame(managerCloseRafRef.current); managerCloseRafRef.current = null; }
    if (managerCloseTimerRef.current) { clearTimeout(managerCloseTimerRef.current); managerCloseTimerRef.current = null; }
  };
  useEffect(() => cancelManagerClose, []);
  // Toggle manage mode. Opening is instant. Closing while scrolled QUICKLY slides
  // the list up to the top (a short rAF scroll — `behavior:'smooth'` isn't
  // honored everywhere), then collapses; collapsing at the top avoids the
  // scrollTop clamp that teleported + left a ghost. A fallback timer still
  // collapses it if rAF is paused (backgrounded tab). Already-at-top / reduced
  // motion close instantly.
  const closeManager = useCallback((then?: () => void) => {
    const el = railScrollRef.current;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const finish = () => { cancelManagerClose(); setManagerSection(null); then?.(); };
    if (el && el.scrollTop > 0 && !reduceMotion) {
      const start = el.scrollTop;
      const t0 = performance.now();
      const DURATION = 240;
      const easeInOutSine = (p: number) => -(Math.cos(Math.PI * p) - 1) / 2;
      const step = (now: number) => {
        const p = Math.min(1, (now - t0) / DURATION);
        el.scrollTop = start * (1 - easeInOutSine(p));
        if (p < 1) managerCloseRafRef.current = requestAnimationFrame(step);
        else finish();
      };
      managerCloseRafRef.current = requestAnimationFrame(step);
      managerCloseTimerRef.current = setTimeout(finish, DURATION + 120);
    } else {
      if (el) el.scrollTop = 0;
      finish();
    }
  }, []);

  // Top sliders — opens individual-only view (email/calendar/drive).
  const toggleManager = useCallback(() => {
    cancelManagerClose();
    if (managerSection === 'individual') { closeManager(); return; }
    setManagerSection('individual');
  }, [managerSection, closeManager]);

  // Bottom sliders — opens team-only toggles.
  const toggleTeamManager = useCallback(() => {
    cancelManagerClose();
    if (managerSection === 'team') { closeManager(); return; }
    setManagerSection('team');
  }, [managerSection, closeManager]);

  // Manage mode owns Escape while open (escape-layer contract, #5266):
  // useEscapeKey preventDefaults so no other Escape consumer double-fires, and
  // the container's `rail-manager-overlay` token (railModulesScrollClassName)
  // makes useModulePaneEscapeClose's DOM-presence guard decline regardless of
  // window-listener registration order. One keypress, one layer: Esc exits
  // manage mode first; a later Esc closes the module pane.
  useEscapeKey(closeManager, isManagerOpen);
  // When the rail is collapsed, hovering anywhere over it morphs the top
  // (workspace) icon into the expand-sidebar glyph — a cue that clicking
  // (the icon or any empty space) expands the rail.
  const [railHovered, setRailHovered] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // Channel/DM unread state now flows from NotificationsProvider's single shared
  // poll (#4894) — the rail no longer runs its own listChannels/listDmChannels
  // loop. Only the rail-specific space-member roster is still fetched here.
  const [spaceMembers, setSpaceMembers] = useState<SpaceMember[] | null>(null);
  const [isWorkspaceDropdownOpen, setIsWorkspaceDropdownOpen] = useState(false);
  const [isAddWorkspaceModalOpen, setIsAddWorkspaceModalOpen] = useState(false);
  const workspaceDropdownRef = useRef<HTMLDivElement>(null);
  const { profile, token, selectedContainer, containers, selectContainer, fetchContainers } = useSession();
  const { showToast } = useToast();
  const { activeConversation, allConversations, setActiveConversation, pluginInstances } = useConversation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { moduleId: currentModuleId } = useParams<{ moduleId?: string }>();
  // Live pane state — the URL's moduleId can be stale (closing a module to
  // chat-only doesn't clear the route), so gate it on whether a module is
  // actually open before preserving it across a conversation switch.
  const { isModulePaneOpen, dockChat } = useChatDock();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const isTeamWorkspace = selectedContainer?.type === 'team';
  // Entitlement gate (ADR 042 §13, WEB-8): a module whose feature verdict is not
  // `granted` gets no rail launch affordance (default-deny).
  const isModuleTypeHidden = useIsModuleTypeHidden(workspaceId);
  // A rail KEY is entitlement-hidden when every module type it represents is
  // hidden (the workspace can't launch ANY of them) — the entitlement
  // replacement for the retired TEAM_ONLY / PERSONAL_ONLY / canSeeClients sets
  // (see SidebarRail.constants.ts). Rail keys backed by no catalogued type stay
  // visible; flash-safe (false while the projection loads).
  const isRailKeyHidden = useCallback(
    (railKey: string): boolean => {
      const types = RAIL_MODULE_TYPES[railKey];
      if (!types || types.length === 0) return false;
      return types.every((t) => isModuleTypeHidden(t));
    },
    [isModuleTypeHidden],
  );

  // Channel/DM unread state from the shared NotificationsProvider poll (#4894).
  // markChannelReadFromContext optimistically clears unread for both the rail
  // badge and the header bell dot in one place.
  const {
    channels: railChannels,
    dms: railDms,
    markChannelRead: markChannelReadFromContext,
  } = useNotificationsContext();

  const displayName = profile?.name?.trim() || profile?.email?.split('@')[0] || 'Account';
  const initials = displayName
    .split(/\s+/)
    .map((w: string) => w.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Fetch workspace modules
  const { data: workspaceModules = [] } = useQuery({
    queryKey: queryKeys.modules.list(workspaceId),
    queryFn: () => api.workspaces.listWorkspaceModules(token ?? null, workspaceId, { includeArchived: false }),
    enabled: Boolean(token && workspaceId),
    staleTime: 30_000,
    placeholderData: (prev: WorkspaceModule[] | undefined) => prev,
  });

  // Fetch module sidebar preferences (used to detect pinned primary-rail modules for archetypes / liquid workspaces)
  const { data: modulePreferences = [] } = useQuery({
    queryKey: queryKeys.modules.sidebarPreferences(workspaceId),
    queryFn: () => api.workspaces.listModuleSidebarPreferences(token ?? null, workspaceId),
    enabled: Boolean(token && workspaceId),
    staleTime: 15_000,
    placeholderData: (prev: ModuleSidebarPreference[] | undefined) => prev,
  });

  // Demo data is onboarding-only: "Use demo data" lives in ChatBox; "Remove demo
  // data" renders via <RemoveDemoDataButton/> (shared useDemoMode() hook). Keeping
  // it out of here avoids the unconditional /demo-seed/status fetch that 400'd in
  // containerized dev (9080).

  // Auto-ensure a 'sidebar_settings' module always exists
  const sidebarSettingsModule = useMemo(
    () => workspaceModules.find(m => m.type === 'sidebar_settings' && !m.archivedAt),
    [workspaceModules],
  );
  const sidebarAutoCreateRef = useRef<string | false>(false);
  const autoCreateInflightRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (sidebarAutoCreateRef.current && sidebarAutoCreateRef.current !== workspaceId) {
      sidebarAutoCreateRef.current = false;
    }
    if (sidebarSettingsModule || sidebarAutoCreateRef.current || !token || !workspaceId || workspaceModules.length === 0) return;
    sidebarAutoCreateRef.current = workspaceId;
    api.workspaces.createWorkspaceModule(token, workspaceId, {
      type: 'sidebar_settings',
      label: 'Sidebar Settings',
      config: { sidebarVisibility: { ...DEFAULT_VISIBILITY } },
      position: -1,
    }).then(() => {
      queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
    }).catch((err) => {
      console.warn('[SidebarRail] Failed to auto-create sidebar_settings:', err);
    });
  }, [sidebarSettingsModule, token, workspaceId, queryClient, workspaceModules.length]);

  // Derive visibility map: merge defaults with stored config.
  // In team workspaces, visibility is per-member so each user can independently
  // show/hide modules without affecting other members.
  const visibility = useMemo(() => {
    const visKey = isTeamWorkspace ? `sidebarVisibility_${profile?.id}` : 'sidebarVisibility';
    const stored = aliasLegacyRailVisibility((sidebarSettingsModule?.config?.[visKey] ?? {}) as Record<string, boolean>);
    // Optimistic overlay wins until the server value catches up — gives the
    // manage-mode toggle instant feedback (dim + slide) with no round-trip wait.
    return { ...DEFAULT_VISIBILITY, ...stored, ...optimisticVis };
  }, [sidebarSettingsModule, optimisticVis, isTeamWorkspace, profile?.id]);

  // Drop optimistic overrides once the refetched config agrees, so a later
  // server-side change (e.g. another tab) isn't masked by a stale optimism.
  useEffect(() => {
    const visKey = isTeamWorkspace ? `sidebarVisibility_${profile?.id}` : 'sidebarVisibility';
    const stored = aliasLegacyRailVisibility((sidebarSettingsModule?.config?.[visKey] ?? {}) as Record<string, boolean>);
    setOptimisticVis((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const [key, val] of Object.entries(prev)) {
        const serverVal = stored[key] ?? DEFAULT_VISIBILITY[key as keyof typeof DEFAULT_VISIBILITY];
        if (serverVal === val) { delete next[key]; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [sidebarSettingsModule, isTeamWorkspace, profile?.id]);

  // Module enable/disable is a CSS-only "light on / light off": the rail item
  // simply un-greys (on) or greys out (`.rail-item-group.dimmed`, off). No
  // JS-driven slide animation — it double-fired on the optimistic-update +
  // refetch and glitched.

  // Primary rail modules for archetypes / liquid workspaces.
  // Detected via: explicit isPrimaryRail flag on module (clean model — camelCase on the wire),
  // or `pinned: true` in user ModuleSidebarPreference (seeder transition signal via /module-preferences).
  // These appear as independent top-level RailItems (own label + type icon) and are excluded from core groupings.
  const primaryModules = useMemo(() => {
    const pinnedModuleIds = new Set(
      modulePreferences.filter((p) => p.pinned).map((p) => p.moduleId)
    );
    return workspaceModules.filter((m) => isPrimaryRailModule(m, pinnedModuleIds));
  }, [workspaceModules, modulePreferences]);

  const primaryModuleIds = useMemo(() => new Set(primaryModules.map((m) => m.id)), [primaryModules]);

  // Core rail keys whose module types have been (at least partially) promoted to primary rail items.
  // Used to suppress the generic core button (e.g. "Tasks") in archetype workspaces so only the custom-labeled
  // primary (e.g. "Chapter Work") appears. If non-primary modules of the type remain, the core group can still show them.
  const replacedCoreKeys = useMemo(() => {
    const set = new Set<string>();
    for (const m of primaryModules) {
      for (const [key, types] of Object.entries(RAIL_MODULE_TYPES)) {
        if (types.includes(m.type)) {
          set.add(key);
          break;
        }
      }
    }
    return set;
  }, [primaryModules]);

// Build lookup: rail key → all matching workspace modules (non-archived)
  // When a 'places' container exists, hide individual place_list modules (accessed as folders inside)
  // Primary rail modules (pinned / isPrimaryRail) are excluded so they don't appear inside classic containers.
  const modulesByRailKey = useMemo(() => {
    const map: Record<string, WorkspaceModule[]> = {};
    for (const [key, types] of Object.entries(RAIL_MODULE_TYPES)) {
      map[key] = workspaceModules.filter((m) => {
        if (primaryModuleIds.has(m.id)) return false; // promoted to dedicated primary rail button
        if (!types.includes(m.type) || m.archivedAt) return false;
        if (isModuleTypeHidden(m.type)) return false; // entitlement-hidden (ADR 042)
        // KEEP place_list / report / html_viewer in their container's rail key
        // (places / pages) even when a container exists. They were previously
        // filtered out, which broke activeRailKey: viewing a report/place_list left
        // its container rail item UNHIGHLIGHTED. They're never shown as separate
        // sub-items (pages/places render subItems:[]), and clicking the rail key
        // opens the container by type (see handleRailClick) — so keeping them here
        // is highlight-only and can't open the wrong module.
        return true;
      });
    }
    return map;
  }, [workspaceModules, primaryModuleIds, isModuleTypeHidden]);

  // Collect plugin/supplemental modules not claimed by any core rail key
  const pluginModules = useMemo(() => {
    const byType: Record<string, WorkspaceModule[]> = {};
    for (const m of workspaceModules) {
      if (m.archivedAt || ALL_CORE_MODULE_TYPES.has(m.type) || m.type === 'sidebar_settings') continue;
      if (isModuleTypeHidden(m.type)) continue; // entitlement-hidden (ADR 042)
      if (!byType[m.type]) byType[m.type] = [];
      byType[m.type].push(m);
    }
    return byType;
  }, [workspaceModules, isModuleTypeHidden]);

  // The module-pane authority (ADR 028). The rail's highlight derives from it
  // directly — the old event-fed local trio (pendingModuleId / focusedModuleId
  // / modulePaneClosedId) is gone, so the highlight can no longer drift from
  // what the pane is actually doing. openPane gives the same instant click
  // feedback the optimistic pendingModuleId used to (it flips the authority
  // synchronously). (renderedModuleId was consumed by the rail's former
  // toggle-close gate; rail clicks now always open/stay open, so the rail no
  // longer reads it.)
  const {
    paneModuleId,
    isPaneVisible,
    openPane,
  } = useModulePane();

  // The module the rail should treat as active: the pane's target while the
  // pane is open. Plugin-instance panes (`plugin:<instanceId>` URLs) render
  // off the route rather than the pane authority, so they fall back to the
  // URL param.
  const effectiveModuleId = (isPaneVisible && paneModuleId)
    ? paneModuleId
    : (currentModuleId?.startsWith('plugin:') ? currentModuleId : undefined);

  // Map the active module to its rail item.
  const activeRailKey = useMemo(() => {
    const currentModuleId = effectiveModuleId;
    if (!currentModuleId) return null;

    // Check plugins
    for (const plugin of pluginInstances) {
      if (`plugin:${plugin.id}` === currentModuleId) {
        for (const [key, pluginId] of Object.entries(RAIL_PLUGIN_IDS)) {
          if (plugin.pluginId === pluginId) return key;
        }
      }
    }

    // Check dynamic primary rail modules (these take precedence; their IDs are excluded from modulesByRailKey)
    for (const m of primaryModules) {
      if (m.id === currentModuleId) return `primary:${m.id}`;
    }

    // Check all module-backed rail items (core groupings; primaries excluded)
    for (const [key, modules] of Object.entries(modulesByRailKey)) {
      if (modules.some((m) => m.id === currentModuleId)) return key;
    }

    // Check plugin modules
    for (const [type, modules] of Object.entries(pluginModules)) {
      if (modules.some((m) => m.id === currentModuleId)) return `plugin:${type}`;
    }

    return null;
  }, [effectiveModuleId, pluginInstances, modulesByRailKey, pluginModules, primaryModules]);

  // --- Navigation helpers ---

  const ensureModulePanelAttached = useCallback(async (
    conversationId: string,
    moduleId: string,
  ): Promise<boolean> => {
    if (!token || !workspaceId) return false;
    try {
      const panels = await api.workspaces.listConversationPanels(
        token, workspaceId, conversationId, { includeArchived: true },
      );
      const activePanel = panels.find((p) => !p.archivedAt && p.moduleId === moduleId);
      if (activePanel) return true;

      const existingPanel = panels.find((p) => p.moduleId === moduleId);
      if (existingPanel) {
        await api.workspaces.updateConversationPanel(
          token, workspaceId, conversationId, existingPanel.id,
          { archived: false, sortOrder: existingPanel.sortOrder },
        );
        return true;
      }

      const maxSortOrder = panels.reduce((max, p) =>
        typeof p.sortOrder === 'number' && p.sortOrder > max ? p.sortOrder : max, -1);

      await api.workspaces.createConversationPanel(
        token, workspaceId, conversationId,
        { workspaceModuleId: moduleId, panelKey: `module:${moduleId}`, sortOrder: maxSortOrder + 1 },
      );
      return true;
    } catch (error) {
      console.warn('[SidebarRail] Failed to ensure module panel attachment', error);
      return false;
    }
  }, [token, workspaceId]);

  const openModule = useCallback(async (moduleId: string) => {
    const targetConversationId = activeConversation?.id ?? allConversations[0]?.id;
    if (!targetConversationId) {
      // A valid click must never silently do nothing (ADR 028). No
      // conversation exists yet (initial load / cleared workspace) — say so.
      showToast('Still loading your workspace — try that module again in a moment.', 'info');
      return;
    }
    const isSameConversation = activeConversation?.id === targetConversationId;

    // SSOT: record the user's intent in the pane authority FIRST. The
    // authority MIRROR flips synchronously (MainLayout's URL hold and any
    // same-tick reader see it via getPaneState()); the rendered highlight +
    // pane follow within the View Transition's commit. A background hydration
    // read can no longer overwrite this click (intent guard in
    // ModulePaneContext).
    openPane(moduleId);

    // OPTIMISTIC: navigate + focus immediately — never gate the pane flip
    // on attach/snapshot network (see useModuleLaunch for the rationale;
    // this is the rail twin of that flow).
    navigate(buildConversationPath(workspaceId, targetConversationId, { moduleId }));
    if (isSameConversation) {
      // ChatBox's module.focus path owns attach+refresh when the module
      // isn't bound yet; dispatch is same-conversation-only because the
      // listener reads the current conversation.
      window.dispatchEvent(new CustomEvent('xavior:force-module-focus', { detail: { moduleId } }));
    } else {
      // Conversation switch: URL hydration seeds the pane; attach the panel, then
      // delegate the conversation refresh to ConversationContext — the SINGLE
      // fetcher/writer of activeConversation (ADR-028 spirit). It re-fetches
      // conversations.detail(id) internally (populating the cache App's readiness
      // gate reads) and applies it under its monotonic switch-sequence guard
      // (last-write-wins), so the rail no longer hand-rolls a second, unguarded,
      // un-cached snapshot fetch+write that could resolve out of order.
      void (async () => {
        const attached = await ensureModulePanelAttached(targetConversationId, moduleId);
        if (!attached) {
          // Same ADR 028 rule as the no-conversation branch above: a click
          // must never silently do nothing. Without this the pane targets the
          // new module but the conversation never switches — the user stares
          // at the stale module until the missing-module auto-clear reverts it.
          showToast("Couldn't open that module here — check your connection and try again.", 'error');
          return;
        }
        // Always apply the TARGET conversation — the list object when present,
        // else a minimal stub (never the stale closure's activeConversation);
        // ConversationContext's detail fetch backfills the name + modules.
        const target = allConversations.find((c) => c.id === targetConversationId)
          ?? { id: targetConversationId, name: '', modules: [] };
        setActiveConversation(target);
      })();
    }
  }, [activeConversation, allConversations, ensureModulePanelAttached, token, workspaceId, setActiveConversation, navigate, openPane, showToast]);

  // Channel/DM unread polling lives in NotificationsProvider (#4894). The rail
  // only needs the space-member roster (rail-only — names the DM list rows), so
  // it polls just that, on the same cadence + visibility gate as the bell poll.
  useEffect(() => {
    if (!workspaceId || !token) return;
    let cancelled = false;
    // Clear the prior workspace's roster so the DM list never flashes the old
    // members (provider resets railDms to null on the same switch — #4894).
    setSpaceMembers(null);
    const fetchSpaceMembers = () => {
      // Skip polling while the tab is backgrounded; resumes on visibilitychange.
      if (typeof document !== 'undefined' && document.hidden) return;
      getSpaceMembers(token, workspaceId)
        .then((members) => { if (!cancelled) setSpaceMembers(members); })
        .catch(() => {});
    };
    fetchSpaceMembers();
    const interval = setInterval(fetchSpaceMembers, POLL_INTERVAL);
    const onVisible = () => { if (!document.hidden) fetchSpaceMembers(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [workspaceId, token]);

  const handleChannelSubItemClick = useCallback((itemId: string) => {
    const channelId = itemId.replace(/^ch:/, '');
    const channelsMods = modulesByRailKey['channels'] ?? [];
    if (channelsMods.length === 0) return;
    // Write to localStorage before openModule so ChannelsModule picks up the
    // correct channel on cold mount (before the focus-channel event fires).
    if (workspaceId) {
      localStorage.setItem(`xavior:lastChannel:${workspaceId}`, channelId);
      localStorage.removeItem(`xavior:lastDmUser:${workspaceId}`);
    }
    void openModule(channelsMods[0].id);
    dockChat();
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('xavior:focus-channel', { detail: { channelId } }));
    }, 120);
    // Optimistic read-clear lives in NotificationsProvider (#4894) so it clears
    // unread for both the rail badge and the header bell dot in one place.
    markChannelReadFromContext(channelId);
  }, [modulesByRailKey, openModule, dockChat, workspaceId, markChannelReadFromContext]);

  const handleDmSubItemClick = useCallback((userId: string) => {
    const channelsMods = modulesByRailKey['channels'] ?? [];
    if (channelsMods.length === 0) return;
    // Write before openModule so ChannelsModule picks up the DM on cold mount.
    if (workspaceId) {
      localStorage.setItem(`xavior:lastDmUser:${workspaceId}`, userId);
      localStorage.removeItem(`xavior:lastChannel:${workspaceId}`);
    }
    void openModule(channelsMods[0].id);
    dockChat();
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('xavior:focus-channel', { detail: { userId } }));
    }, 120);
  }, [modulesByRailKey, openModule, dockChat, workspaceId]);

  // Listen for notification toasts requesting channel navigation
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ channelId?: string; isDm?: boolean; otherUserId?: string }>).detail
      const channelId = detail?.channelId
      if (!channelId) {
        // Bell click with no specific channel — just open the Channels module
        const channelsMods = modulesByRailKey['channels'] ?? []
        if (channelsMods.length > 0) void openModule(channelsMods[0].id)
        return
      }
      if (detail?.isDm && detail.otherUserId) {
        // Route DM toasts through the DM handler so lastDmUser suppression key is preserved
        handleDmSubItemClick(detail.otherUserId)
      } else {
        handleChannelSubItemClick(channelId)
      }
    }
    window.addEventListener('xavior:open-channel-module', handler)
    return () => window.removeEventListener('xavior:open-channel-module', handler)
  }, [handleChannelSubItemClick, handleDmSubItemClick, modulesByRailKey, openModule])

  const navigateToPlugin = useCallback((railKey: string) => {
    const pluginId = RAIL_PLUGIN_IDS[railKey];
    if (!pluginId) return;

    const instance = pluginInstances.find((p) => p.pluginId === pluginId);
    if (!instance) return;

    const targetConversationId = activeConversation?.id ?? allConversations[0]?.id;
    if (!targetConversationId) return;

    const moduleId = `plugin:${instance.id}`;
    navigate(buildConversationPath(workspaceId, targetConversationId, { moduleId }));
    window.dispatchEvent(new CustomEvent('xavior:force-module-focus', { detail: { moduleId } }));
  }, [pluginInstances, activeConversation, allConversations, workspaceId, navigate]);

  // --- Click handler: 1 module → direct nav, 2+ → inline expand (expanded) or flyout (collapsed) ---

  const handleRailClick = useCallback((railKey: string) => {
    // Intentionally not toggling closed when clicking the active rail item —
    // clicking a module in the rail always opens/stays open (never closes).

    // Plugin-backed items always direct-nav
    if (RAIL_PLUGIN_IDS[railKey]) {
      setOpenFlyout(null);
      navigateToPlugin(railKey);
      return;
    }

    // Dynamic primary rail modules (promoted via isPrimaryRail or pinned) — direct nav to the specific module, like first-class buttons
    if (railKey.startsWith('primary:') || railKey.startsWith('module:')) {
      const moduleId = railKey.split(':')[1];
      setOpenFlyout(null);
      openModule(moduleId);
      return;
    }

    // Channels / DMs: open module and restore last channel (always expanded, no toggle)
    if ((railKey === 'channels' || railKey === 'dms') && (modulesByRailKey['channels'] ?? []).length > 0) {
      setOpenFlyout(null);
      const channelsMods = modulesByRailKey['channels'] ?? [];
      if (channelsMods.length > 0) {
        void openModule(channelsMods[0].id);
        dockChat();
        const lastChannelId = workspaceId ? localStorage.getItem(`xavior:lastChannel:${workspaceId}`) : null;
        if (lastChannelId) {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('xavior:focus-channel', { detail: { channelId: lastChannelId } }));
          }, 120);
        }
      }
      return;
    }

    // Resolve modules for this rail key
    let modules: WorkspaceModule[] = [];
    if (railKey.startsWith('plugin:')) {
      const type = railKey.slice(7);
      modules = pluginModules[type] ?? [];
    } else {
      modules = modulesByRailKey[railKey] ?? [];
    }

    // No module exists for this rail key — auto-create it
    if (modules.length === 0 && !railKey.startsWith('plugin:')) {
      // Map rail keys to checklist keys where they differ (e.g., files → drive)
      const checklistKey = ({ files: 'drive' } as Record<string, string>)[railKey] ?? railKey;
      const meta = CHECKLIST_MODULE_TYPES[checklistKey];
      if (meta && token && workspaceId && !autoCreateInflightRef.current.has(checklistKey)) {
        autoCreateInflightRef.current.add(checklistKey);
        (async () => {
          try {
            // Re-fetch to avoid duplicates (API may have seeded modules since last render)
            const freshModules = await api.workspaces.listWorkspaceModules(token, workspaceId, { includeArchived: false });
            const alreadyExists = freshModules.some(m => m.type === meta.type && !m.archivedAt);
            if (alreadyExists) {
              await queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
              const existing = freshModules.find(m => m.type === meta.type && !m.archivedAt);
              if (existing) openModule(existing.id);
              return;
            }
            const config = meta.type === 'drive' ? { visibility: 'private' } : {};
            const created = await api.workspaces.createWorkspaceModule(token, workspaceId, {
              type: meta.type,
              label: meta.label,
              position: 0,
              config,
            });
            await queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
            if (created?.id) {
              openModule(created.id);
            }
          } catch (err) {
            console.warn(`[SidebarRail] Failed to auto-create ${meta.type} module:`, err);
          } finally {
            autoCreateInflightRef.current.delete(checklistKey);
          }
        })();
        return;
      }
    }

    // Drive: always navigate directly to the drive module (photos/docs are accessed inside Drive)
    if (railKey === 'files') {
      const driveModule = modules.find((m) => m.type === 'drive' && (m.config as any)?.isRoot)
        || modules.find((m) => m.type === 'drive');
      if (driveModule) {
        setOpenFlyout(null);
        openModule(driveModule.id);
        return;
      }
    }

    // Places: always navigate directly to the places container (place_list folders are accessed inside)
    if (railKey === 'places') {
      const placesModule = modules.find((m) => m.type === 'places');
      if (placesModule) {
        setOpenFlyout(null);
        openModule(placesModule.id);
        return;
      }
    }

    // Pages: always navigate to the pages container (report/html_viewer modules are accessed inside it)
    // If no pages container exists yet (legacy workspaces), auto-create one
    if (railKey === 'pages') {
      const pagesModule = modules.find((m) => m.type === 'pages');
      if (pagesModule) {
        setOpenFlyout(null);
        openModule(pagesModule.id);
        return;
      }
      // No pages container — auto-create it (even if report/html_viewer modules exist)
      if (token && workspaceId && !autoCreateInflightRef.current.has('pages')) {
        autoCreateInflightRef.current.add('pages');
        (async () => {
          try {
            const created = await api.workspaces.createWorkspaceModule(token, workspaceId, {
              type: 'pages',
              label: 'Studio',
              position: 0,
              config: {},
            });
            await queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
            if (created?.id) {
              setOpenFlyout(null);
              openModule(created.id);
            }
          } catch (err) {
            console.warn('[SidebarRail] Failed to auto-create pages container:', err);
          } finally {
            autoCreateInflightRef.current.delete('pages');
          }
        })();
      }
      // Always return — don't fall through to single-module fallback while creation is in-flight
      return;
    }

    // Health: always navigate directly to the health container
    if (railKey === 'health') {
      const healthModule = modules.find((m) => m.type === 'health');
      if (healthModule) {
        setOpenFlyout(null);
        openModule(healthModule.id);
        return;
      }
    }

    // Synthesis: always navigate directly to the synthesis module
    if (railKey === 'synthesis') {
      const synthesisModule = modules.find((m) => m.type === 'synthesis');
      if (synthesisModule) {
        setOpenFlyout(null);
        openModule(synthesisModule.id);
        return;
      }
    }

    // Single module: go directly to it
    if (modules.length === 1) {
      setOpenFlyout(null);
      openModule(modules[0].id);
      return;
    }

    // 2+ modules: inline expand when sidebar is expanded, flyout when collapsed
    if (modules.length >= 2) {
      if (expanded) {
        setExpandedGroups((prev) => {
          const next = new Set(prev);
          if (next.has(railKey)) next.delete(railKey);
          else next.add(railKey);
          return next;
        });
      } else {
        setOpenFlyout((prev) => (prev === railKey ? null : railKey));
      }
    }
  }, [navigateToPlugin, modulesByRailKey, pluginModules, openModule, expanded, token, workspaceId, queryClient]);

  // Hover/focus warmup: kick off the lazy chunk + primary data query for the
  // module a rail item points at, BEFORE the click. Mirrors handleRailClick's
  // resolution (same branches, same single-module target) but is fully
  // additive and fire-and-forget — it never navigates and never touches the
  // synchronous click path. Where a rail key maps to 2+ modules (a flyout/
  // group), the target instance is ambiguous, so we warm the chunk only and
  // skip the (instance-scoped) data prefetch.
  const handleRailHover = useCallback((railKey: string) => {
    // Plugin-backed items: no chunk/data recipe today (RAIL_PLUGIN_IDS is empty).
    if (RAIL_PLUGIN_IDS[railKey]) return;

    // Dynamic primary rail modules — resolve the concrete instance to get its type.
    if (railKey.startsWith('primary:') || railKey.startsWith('module:')) {
      const moduleId = railKey.split(':')[1];
      const mod = workspaceModules.find((m) => m.id === moduleId);
      if (!mod) return;
      prefetchModuleChunk(mod.type);
      prefetchModuleData(queryClient, mod.type, { token, moduleId: mod.id });
      return;
    }

    // Resolve the modules backing this rail key.
    let modules: WorkspaceModule[] = [];
    if (railKey.startsWith('plugin:')) {
      modules = pluginModules[railKey.slice(7)] ?? [];
    } else {
      modules = modulesByRailKey[railKey] ?? [];
    }

    if (modules.length === 1) {
      const mod = modules[0];
      prefetchModuleChunk(mod.type);
      prefetchModuleData(queryClient, mod.type, { token, moduleId: mod.id });
      return;
    }

    // Container rail keys (Drive/Places/Pages/Health/Synthesis) direct-nav to a
    // single container even when sub-modules exist — warm that container.
    const container = modules.find((m) =>
      (railKey === 'files' && m.type === 'drive')
      || (railKey === 'places' && m.type === 'places')
      || (railKey === 'pages' && m.type === 'pages')
      || (railKey === 'health' && m.type === 'health')
      || (railKey === 'synthesis' && m.type === 'synthesis'),
    );
    if (container) {
      prefetchModuleChunk(container.type);
      prefetchModuleData(queryClient, container.type, { token, moduleId: container.id });
      return;
    }

    // Multi-module group (flyout) — instance is ambiguous, so warm only the
    // chunk for the rail key's mapped type (no-op for bundled modules).
    const fallbackType = modules[0]?.type
      ?? CHECKLIST_MODULE_TYPES[railKeyToVisibilityKey[railKey] ?? railKey]?.type;
    if (fallbackType) prefetchModuleChunk(fallbackType);
  }, [workspaceModules, pluginModules, modulesByRailKey, queryClient, token]);

  const closeFlyout = useCallback(() => {
    setOpenFlyout(null);
  }, []);

  const handleSubItemClick = useCallback((moduleId: string) => {
    openModule(moduleId);
  }, [openModule]);

  const handleFlyoutSelect = useCallback(async (railKey: string, mod: WorkspaceModule) => {
    setOpenFlyout(null);
    await openModule(mod.id);
  }, [openModule]);

  const handleVisibilityToggle = useCallback(async (railKey: string, checked: boolean) => {
    if (!token || !workspaceId || !sidebarSettingsModule) return;

    // The boundary slide is a REAL order change, applied to the persisted rail
    // order so edit mode and normal mode always read the same:
    //  • disabling stamps the module so it rises to the top of the off group;
    //  • re-enabling clears that stamp AND moves the module to the BOTTOM of the
    //    rail order (the slot the row slides into, in both views).
    // sidebarOrder is keyed by rail key — map the visibility key to it: plugins
    // use `plugin:${type}`, core remaps drive→files, everything else is identity.
    const pluginType = railKey === 'shopify' ? 'shopify-crm' : railKey;
    const railKeyForOrder = PLUGIN_MODULE_META[pluginType]
      ? `plugin:${pluginType}`
      : visibilityKeyToRailKey(railKey, railKeyToVisibilityKey);

    // Don't let the user disable the LAST enabled module — a workspace needs at
    // least one. Shake the row to signal it instead of turning it off.
    if (!checked && orderedRailKeysRef.current.length <= 1) {
      if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
      setShakeKey(railKeyForOrder);
      shakeTimerRef.current = setTimeout(() => setShakeKey(null), 500);
      return;
    }

    let newOrder: string[] | null = null;
    if (checked) {
      setOffStamp((s) => { const n = { ...s }; delete n[railKey]; return n; });
      // Place the re-enabled module after every currently-visible on item (the
      // true bottom of the on list), then merge hidden keys back at their slots.
      const prevOrder = aliasLegacyRailOrder((sidebarSettingsModule.config?.sidebarOrder ?? []) as string[]);
      const desiredOrder = [...orderedRailKeysRef.current.filter((k) => k !== railKeyForOrder), railKeyForOrder];
      newOrder = mergeRailOrder(desiredOrder, prevOrder);
      setTempRailOrder(newOrder); // optimistic — bottom-of-on this frame, both views
    } else {
      const seq = (stampSeqRef.current += 1);
      setOffStamp((s) => ({ ...s, [railKey]: seq }));
      // If the module being disabled is the one currently open in the pane,
      // force the user back to chat-only — its rail item is about to
      // disappear. The highlight clears on its own: hideModulePane flips the
      // pane authority, which is what the rail renders from (ADR 028).
      if (activeRailKey === railKeyForOrder) {
        window.dispatchEvent(new CustomEvent(conversationEvents.closeModulePane));
      }
    }
    // Apply visibility optimistically so the row dims/un-dims and slides to its
    // boundary this frame; the PATCH + refetch below reconciles it.
    setOptimisticVis((s) => ({ ...s, [railKey]: checked }));

    const newVisibility = { ...visibility, [railKey]: checked };
    try {
      // Create module first (if needed) so visibility isn't saved without a module
      if (checked) {
        const meta = CHECKLIST_MODULE_TYPES[railKey];
        if (meta) {
          const exists = workspaceModules.some(m => m.type === meta.type && !m.archivedAt);
          if (!exists) {
            await api.workspaces.createWorkspaceModule(token, workspaceId, {
              type: meta.type,
              label: meta.label,
              position: 0,
            });
          }
        }
      }

      // Visibility + (on re-enable) the new order go in ONE PATCH so the two
      // config writes can't clobber each other. In team workspaces, write
      // visibility under a per-user key so members toggle independently.
      const visKey = isTeamWorkspace ? `sidebarVisibility_${profile?.id}` : 'sidebarVisibility';
      await api.workspaces.updateWorkspaceModule(token, workspaceId, sidebarSettingsModule.id, {
        config: {
          ...sidebarSettingsModule.config,
          [visKey]: newVisibility,
          ...(newOrder ? { sidebarOrder: newOrder } : {}),
        },
      });

      await queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
    } catch (err) {
      console.warn('[SidebarRail] Failed to update module visibility:', err);
    }
  }, [token, workspaceId, sidebarSettingsModule, visibility, workspaceModules, queryClient, activeRailKey, isTeamWorkspace, profile?.id]);

  // Close workspace dropdown on outside click
  useEffect(() => {
    if (!isWorkspaceDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (workspaceDropdownRef.current && !workspaceDropdownRef.current.contains(e.target as Node)) {
        setIsWorkspaceDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isWorkspaceDropdownOpen]);

  const getContainerDisplayName = useCallback((container: typeof containers[0]) => {
    if (container.containerName) return container.containerName;
    if (container.isOwner) return 'My Workspace';
    return container.ownerName || container.ownerEmail?.split('@')[0] || 'Shared Space';
  }, []);

  const handleExpand = useCallback(() => {
    setExpanded(true);
    try { localStorage.setItem('xavior:sidebarExpanded', 'true'); } catch { /* ignore */ }
  }, []);

  const handleCollapse = useCallback(() => {
    setExpanded(false);
    try { localStorage.setItem('xavior:sidebarExpanded', 'false'); } catch { /* ignore */ }
  }, []);

  // TODO: wire up unread state from email integration
  const hasUnread = false;
  // Automations rail dot: lit when there's a pending sign-off / blocking question
  // (sourced from the automations module's notification feed, not the bell center).
  const { data: automationNotifs = [] } = useNotifications();
  const automationsHasUnread = automationsRailHasUnread(automationNotifs);


  // Rail items config — ordered to match the onboarding sequence. The stateless
  // SVG icons live in RAIL_ITEM_ICONS (SidebarRail.icons); label/badge stay here
  // because they depend on workspace state (isTeamWorkspace, unread counts).
  const railItems: Array<{ key: string; label: string; badge?: boolean; icon: React.ReactNode }> = [
    { key: 'automations', label: 'Automations', badge: automationsHasUnread, icon: RAIL_ITEM_ICONS.automations },
    { key: 'email', label: 'Email', badge: hasUnread, icon: RAIL_ITEM_ICONS.email },
    { key: 'calendar', label: 'Calendar', icon: RAIL_ITEM_ICONS.calendar },
    { key: 'contacts', label: isTeamWorkspace ? 'Team Contacts' : 'Contacts', icon: RAIL_ITEM_ICONS.contacts },
    { key: 'files', label: 'Drive', icon: RAIL_ITEM_ICONS.files },
    { key: 'tasks', label: isTeamWorkspace ? 'Team Kanban' : 'Kanban', icon: RAIL_ITEM_ICONS.tasks },
    { key: 'pages', label: 'Studio', icon: RAIL_ITEM_ICONS.pages },
    { key: 'places', label: 'Places', icon: RAIL_ITEM_ICONS.places },
    { key: 'notes', label: 'Notes', icon: RAIL_ITEM_ICONS.notes },
    { key: 'health', label: 'Health', icon: RAIL_ITEM_ICONS.health },
    { key: 'synthesis', label: 'Synthesis', icon: RAIL_ITEM_ICONS.synthesis },
    { key: 'channels', label: 'Channels', icon: RAIL_ITEM_ICONS.channels },
    { key: 'dms', label: 'Direct Messages', icon: RAIL_ITEM_ICONS.dms },
    { key: 'clients', label: 'Clients', icon: RAIL_ITEM_ICONS.clients },
  ];

  // Only show rail items that are visible and have at least one module (or visibility explicitly true)
  // Map rail keys to checklist keys where they differ (files → drive) — see SidebarRail.constants.ts
  // Team-only modules (e.g. Channels) are hidden entirely in solo (Free/Pro)
  // workspaces — they only appear in a team workspace (under "TEAM MODULES").
  // isTeamWorkspace is defined above near workspaceId so it's available to hooks.

  const visibleRailItems = railItems.filter((item) => {
    // Default-deny (ADR 042 §13): no launch affordance for a rail key whose
    // module types are all entitlement-ungranted. Replaces the hardcoded
    // TEAM_ONLY / PERSONAL_ONLY / clients-allowlist gates — `channels`/`dms`
    // appear only where `channels` is granted (team workspaces at launch);
    // `notes`/`agents`/`synthesis`/`health`/`clients` stay hidden until granted.
    if (isRailKeyHidden(item.key)) return false;
    const visKey = railKeyToVisibilityKey[item.key] ?? item.key;
    if (visibility[visKey] === false) return false;
    const modules = modulesByRailKey[item.key] ?? [];
    // For archetype/primary-rail workspaces (those with custom primaryModules from the seeder),
    // suppress the generic core button for any promoted type so only the custom-labeled items
    // (Scratch, Chapter Work, Roadmap, Readers & Experts, Writer's Studio Guide, Book Status)
    // appear as the first-class top-level module rail. Classic workspaces remain unchanged.
    if (primaryModules.length > 0 && replacedCoreKeys.has(item.key)) {
      return false;
    }
    // Conservative fallback for non-archetype workspaces
    if (replacedCoreKeys.has(item.key) && modules.length === 0) {
      return false;
    }
    return modules.length > 0 || visibility[visKey] === true;
  });

  // ── Reorder ("move") mode ordering ──────────────────────────────────────
  // The persisted order of rail category keys lives in the sidebar-settings
  // module config (same place as sidebarVisibility). Sort the visible items by
  // it; keys not present keep their config-array order (appended after).
  const storedRailOrder = aliasLegacyRailOrder((sidebarSettingsModule?.config?.sidebarOrder ?? []) as string[]);
  // A workspace with no custom order falls back to DEFAULT_RAIL_ORDER (Email,
  // Calendar, Drive, Contacts, Channels, Tasks, Notes, Pages, Places, then the rest).
  const activeRailOrder = tempRailOrder ?? (storedRailOrder.length > 0 ? storedRailOrder : DEFAULT_RAIL_ORDER);
  const orderedRailItems = sortRailItemsByOrder(visibleRailItems, activeRailOrder);

  // Plugins are first-class rail modules: fold the visible plugin modules into
  // the SAME ordered on-list as core so they drag, reorder, and re-enable to the
  // bottom exactly like every other module — instead of being pinned in a
  // separate trailing block. One uniform shape powers both edit and normal mode.
  type OnRailItem = {
    key: string;        // rail key (core item.key, or `plugin:${type}`) — order id
    visKey: string;     // visibility key — toggle target + stamp key
    label: string;
    icon: React.ReactNode;
    badge?: boolean;
    count: number;
    subItems: Array<{ id: string; label: string }>;
  };
  const coreOnItems: OnRailItem[] = orderedRailItems.map((item) => {
    const isLeaf = LEAF_GROUP_RAIL_KEYS.has(item.key);
    const modules = modulesByRailKey[item.key] ?? [];
    return {
      key: item.key,
      visKey: railKeyToVisibilityKey[item.key] ?? item.key,
      label: item.label,
      icon: item.icon,
      badge: item.badge,
      count: isLeaf ? 0 : modules.length,
      subItems: isLeaf ? [] : modules.map((m) => ({ id: m.id, label: m.label })),
    };
  });
  const pluginOnItems: OnRailItem[] = Object.entries(pluginModules).flatMap(([type, modules]) => {
    if (modules.length === 0) return [];
    const railKeyForPlugin = type === 'shopify-crm' ? 'shopify' : type;
    if (visibility[railKeyForPlugin] === false) return [];
    const meta = PLUGIN_MODULE_META[type];
    return [{
      key: `plugin:${type}`,
      visKey: railKeyForPlugin,
      label: meta?.label ?? modules[0].label ?? type,
      icon: meta?.icon ?? FALLBACK_PLUGIN_ICON,
      count: modules.length,
      subItems: modules.map((m) => ({ id: m.id, label: m.label })),
    }];
  });
  // Combined + ordered by activeRailOrder (plugin keys sort by their saved slot
  // if present, else append after core — same default, but now reorderable).
  const onRailItems = sortRailItemsByOrder([...coreOnItems, ...pluginOnItems], activeRailOrder);
  orderedRailKeysRef.current = onRailItems.map((i) => i.key);

  // Off-group ordering (manage mode only): most-recently-disabled rise to the
  // TOP. The on group is intentionally left in persisted rail order (no stamp
  // re-sort) so it matches the always-shown order while editing.
  const sortOffByStamp = (keys: string[]): string[] =>
    [...keys].sort((a, b) => (offStamp[b] ?? -1) - (offStamp[a] ?? -1));

  // ── Manage mode: one unified, ordered list (on + off) ───────────────────
  // Rendering on AND off rows in a single Reorder.Group, each keyed by its
  // stable rail key, is what makes a toggle smooth: the SAME element stays
  // mounted and Framer's layout animation slides it to its new spot (and CSS
  // fades the dim) instead of it unmounting from one list and popping into the
  // other. Order: turned-on first in persisted rail order (the SAME order shown
  // outside edit mode), then turned-off (most-recently-disabled risen to the top
  // by offStamp). On rows (core AND plugin) are drag-reorderable; off rows ride
  // along (draggable: false) so they still animate but never start a drag.
  type ManageEntry = {
    key: string;        // stable rail key — React key + Reorder value + drag-order id
    visKey: string;     // visibility key — toggle target + on/off stamp key
    isOn: boolean;
    draggable: boolean;
    label: string;
    icon: React.ReactNode;
  };
  const manageEntries: ManageEntry[] = managerSection === null ? [] : (() => {
    // On rows come straight from the unified on-list (core + plugins), all
    // draggable — the exact order shown outside edit mode.
    const onSorted: ManageEntry[] = onRailItems.map((item) => ({
      key: item.key,
      visKey: item.visKey,
      isOn: true,
      draggable: true,
      label: item.label,
      icon: item.icon,
    }));

    // Default-deny (ADR 042 §13): an ungranted rail key is not offered as a
    // re-enable affordance either. computeOffManageKeys maps each visibility key
    // back to its rail key (drive↔files is the only remap) before the entitlement
    // check; the stamp-order sort is applied on top.
    const offKeys = sortOffByStamp(computeOffManageKeys({
      allVisibilityKeys: Object.keys(DEFAULT_VISIBILITY),
      visibility,
      railKeyToVisibilityKey,
      isRailKeyHidden,
    }));
    const offEntries: ManageEntry[] = offKeys.map((visKey) => {
      // Map back to a rail key so the off row shares its on row's key (stable
      // identity across the toggle → smooth move). drive↔files is the only remap.
      const railKey = visibilityKeyToRailKey(visKey, railKeyToVisibilityKey);
      const railItem = railItems.find((r) => r.key === railKey);
      if (railItem) return { key: railItem.key, visKey, isOn: false, draggable: false, label: railItem.label, icon: railItem.icon };
      const pluginType = visKey === 'shopify' ? 'shopify-crm' : visKey;
      const meta = PLUGIN_MODULE_META[pluginType];
      if (meta) return { key: `plugin:${pluginType}`, visKey, isOn: false, draggable: false, label: meta.label, icon: meta.icon };
      return { key: visKey, visKey, isOn: false, draggable: false, label: visKey.charAt(0).toUpperCase() + visKey.slice(1), icon: FALLBACK_PLUGIN_ICON };
    });

    return [...onSorted, ...offEntries];
  })();

  // Drag reorders the unified list; persist the relative order of the on rows
  // (core + plugin) and drop the off rows from the reported order.
  const handleManageReorder = (keys: string[]) => {
    const onKeys = new Set(onRailItems.map((i) => i.key));
    handleRailReorder(keys.filter((k) => onKeys.has(k)));
  };

  // Idle warmup: whenever the set of module types in THIS workspace changes
  // (first load, or a module created mid-session — e.g. agent- or
  // picker-created modules that render inline in chat with no hover
  // opportunity), opportunistically prefetch the lazy chunks
  // (email/channels/tasks/drive/contacts — see LAZY_CHUNK_FACTORIES) so even
  // a no-hover first render is warm. Deferred to idle time so it never
  // competes with first paint. prefetchModuleChunk is idempotent + a no-op
  // for bundled modules, so re-running on type-set changes is nearly free.
  const idleWarmedTypesRef = useRef('');
  useEffect(() => {
    if (!workspaceId || workspaceModules.length === 0) return;
    const lazyTypes = [...new Set(
      workspaceModules
        .filter((m) => !m.archivedAt)
        .map((m) => m.type),
    )].sort();
    const signature = `${workspaceId}|${lazyTypes.join(',')}`;
    if (idleWarmedTypesRef.current === signature) return;
    idleWarmedTypesRef.current = signature;
    const warm = () => {
      for (const t of lazyTypes) prefetchModuleChunk(t);
    };
    // requestIdleCallback isn't in every Safari version — fall back to a short
    // setTimeout (mirrors the repo's window guards elsewhere).
    const win = window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof win.requestIdleCallback === 'function') {
      const id = win.requestIdleCallback(warm);
      return () => win.cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(warm, 800);
    return () => window.clearTimeout(t);
  }, [workspaceId, workspaceModules]);

  // In a TEAM workspace the rail splits into two groups: the private/provider
  // modules (Email, Calendar, Drive) stay under the top "MODULES" header, and
  // everything else (shared Xavior-native data) moves under a "TEAM MODULES"
  // header. Solo workspaces keep the single flat list. The split is suppressed
  // in manager/reorder mode so drag-to-reorder keeps operating on one list.
  const teamRailItems = onRailItems.filter((i) => !PROVIDER_RAIL_KEYS.has(i.key));
  // Team-workspace rail sections (product decision): the INDIVIDUAL (per-member)
  // section is the provider modules + automations; the shared "Team Modules"
  // section is EVERY other non-provider on-rail item (incl. plugins like
  // campaigns), so no granted entitlement-gated module falls through the gap and
  // vanishes on a team workspace (#5455). See SidebarRail.utils.
  const individualRailItems = onRailItems.filter((i) => isIndividualTeamRailKey(i.key));
  const sharedTeamRailItems = sortRailItemsByOrder(
    onRailItems.filter((i) => isSharedTeamRailKey(i.key)),
    TEAM_RAIL_ORDER,
  );

  const persistRailOrder = useCallback(async (orderedKeys: string[]) => {
    if (!token || !workspaceId || !sidebarSettingsModule) return;
    // `orderedKeys` only contains the CURRENTLY-visible categories. Merge back
    // any keys from the saved order that aren't visible right now (e.g. a
    // category toggled off in manager mode) at their previous position, so a
    // reorder never wipes a hidden category's saved slot.
    const prevOrder = aliasLegacyRailOrder((sidebarSettingsModule.config?.sidebarOrder ?? []) as string[]);
    const mergedOrder = mergeRailOrder(orderedKeys, prevOrder);
    try {
      await api.workspaces.updateWorkspaceModule(token, workspaceId, sidebarSettingsModule.id, {
        config: { ...sidebarSettingsModule.config, sidebarOrder: mergedOrder },
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
    } catch (err) {
      // Keep the user's optimistic order on screen (the effect below won't clear
      // tempRailOrder since the stored order didn't change), but tell them it
      // didn't save — otherwise the reorder silently reverts on next load.
      console.warn('[SidebarRail] Failed to persist module order:', err);
      showToast("Couldn't save the new order — please try again.", 'error');
    }
  }, [token, workspaceId, sidebarSettingsModule, queryClient, showToast]);

  // Framer Reorder fires onReorder continuously with the new key order while
  // dragging — mirror it into tempRailOrder (drives the live render) and stash
  // it in a ref so the drag-end handler persists the latest order without a
  // stale closure.
  const handleRailReorder = (keys: string[]) => {
    pendingRailOrderRef.current = keys;
    setTempRailOrder(keys);
  };
  const handleRailReorderEnd = () => {
    if (pendingRailOrderRef.current) {
      void persistRailOrder(pendingRailOrderRef.current);
    }
    pendingRailOrderRef.current = null;
    // NOTE: deliberately do NOT clear tempRailOrder here. Clearing it
    // synchronously would drop back to the stale `storedRailOrder` (the PATCH +
    // refetch hasn't landed yet) and the list would visibly snap back before
    // jumping to the new order. The effect below clears it once the refetched
    // config actually reflects the committed order.
  };

  // Drop the optimistic drag order only once the persisted (refetched) order
  // matches it — keeps the rail on the user's order with no snap-back, and if
  // the PATCH failed it stays on their order instead of silently reverting.
  useEffect(() => {
    if (!tempRailOrder) return;
    // Clear the optimistic order once the refetched stored order reflects it.
    // Compare the stored order RESTRICTED TO tempRailOrder's own keys — which now
    // include plugin (`plugin:*`) and merged-in hidden keys (re-enable goes
    // through mergeRailOrder). Filtering to the visible-core subset instead can
    // never match that wider key set, so tempRailOrder would stick forever and
    // mask later refetched/cross-tab order changes. persistRailOrder/mergeRailOrder
    // both preserve tempRailOrder's relative order, so once the PATCH lands the
    // stored order contains all these keys in the same sequence.
    const tempKeys = new Set(tempRailOrder);
    const storedSubset = storedRailOrder.filter((k) => tempKeys.has(k));
    const matches =
      storedSubset.length === tempRailOrder.length &&
      storedSubset.every((k, i) => k === tempRailOrder[i]);
    if (matches) setTempRailOrder(null);
  }, [storedRailOrder, tempRailOrder]);

  const toTeamLabel = <T extends { key: string; label: string }>(e: T): T =>
    e.key === 'files' ? { ...e, label: 'Team Drive' } :
    e.key === 'calendar' ? { ...e, label: 'Team Calendar' } : e;

  return (
    <>
      <aside
        className={`sidebar-rail${expanded ? ' expanded' : ''}`}
        onClick={!expanded ? (e) => {
          // Clicking any non-button "whitespace" area expands the rail (the
          // cursor over those areas is a right-arrow / e-resize cue).
          if (!(e.target as HTMLElement).closest('button, a, input, .rail-avatar-wrap')) {
            handleExpand();
          }
        } : undefined}
        onMouseEnter={() => setRailHovered(true)}
        onMouseLeave={() => setRailHovered(false)}
      >
        <div className="rail-items">
          {/* 2. Workspace Switcher — hidden for archetype/primary-rail workspaces (Writer's Studio)
              so the rail starts cleanly with New chat / History / "Writer’s Studio" header + the six custom modules. */}
          {primaryModules.length === 0 && containers.length > 0 && (expanded ? (
            <div ref={workspaceDropdownRef} className="rail-item-group" style={{ position: 'relative' }}>
              <div className="rail-workspace-row">
              <button
                className="rail-btn"
                onClick={() => setIsWorkspaceDropdownOpen(o => !o)}
              >
                <span className="rail-btn-icon">
                  {selectedContainer?.isOwner !== false ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" />
                      <path d="M9 22V12H15V22" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" />
                      <circle cx="9" cy="7" r="4" />
                    </svg>
                  )}
                </span>
                <span className="rail-btn-label">
                  {selectedContainer ? getContainerDisplayName(selectedContainer) : 'Workspace'}
                </span>
                <svg className={`rail-chevron${isWorkspaceDropdownOpen ? ' open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
                <button
                  className="rail-collapse-btn"
                  onClick={handleCollapse}
                  title="Collapse sidebar"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                    <line x1="9" y1="3" x2="9" y2="21" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M17 9L14 12L17 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
                {isWorkspaceDropdownOpen && (
                  <div className="sidebar-workspace-dropdown-list">
                    {containers.map((container, index) => {
                      const isSelected = container.id
                        ? selectedContainer?.id === container.id
                        : selectedContainer?.ownerId === container.ownerId;
                      return (
                        <button
                          key={getContainerKey(container, index)}
                          className={`sidebar-workspace-dropdown-option${isSelected ? ' active' : ''}`}
                          onClick={() => {
                            if (!isSelected) void selectContainer(container.id || container.ownerId);
                            setIsWorkspaceDropdownOpen(false);
                          }}
                        >
                          <span style={{ width: 20, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {isSelected && (
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6L9 17L4 12" />
                              </svg>
                            )}
                          </span>
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {getContainerDisplayName(container)}
                          </span>
                          {typeof container.memberCount === 'number' && (
                            <span style={{ fontSize: 11, color: 'var(--color-text-faint, #aaa)', flexShrink: 0, marginLeft: 8 }}>
                              {container.memberCount} {container.memberCount === 1 ? 'member' : 'members'}
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {/* Workspace action — modal handles all limit/upgrade logic */}
                    <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 4, paddingTop: 4 }}>
                      <button
                        className="sidebar-workspace-dropdown-action"
                        onClick={() => {
                          setIsWorkspaceDropdownOpen(false);
                          setIsAddWorkspaceModalOpen(true);
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        New Workspace
                      </button>
                    </div>
                  </div>
                )}
            </div>
          ) : (
            <button
              className="rail-btn"
              onClick={handleExpand}
              title="Expand sidebar"
            >
              <span className="rail-btn-icon">
                {railHovered ? (
                  /* Hovering the collapsed rail: the workspace icon morphs into
                     the expand-sidebar glyph (panel + right-pointing arrow). */
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
                    <line x1="9" y1="3" x2="9" y2="21" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M14 9L17 12L14 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  /* Resting state: the same workspace (home) icon as the
                     expanded workspace row, in the same fixed 48px column. */
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" />
                    <path d="M9 22V12H15V22" />
                  </svg>
                )}
              </span>
              <span className="rail-tooltip">Expand sidebar</span>
            </button>
          ))}

          {/* 3. New Chat */}
          <button
            className="rail-btn"
            onClick={() => { setOpenFlyout(null); onNewChat(); }}
          >
            <span className="rail-btn-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </span>
            <span className="rail-btn-label">New chat</span>
            {!expanded && <span className="rail-tooltip">New chat</span>}
          </button>

          {/* 3. Chat History */}
          <button
            className={`rail-btn${openFlyout === 'history' ? ' active' : ''}`}
            onClick={() => setOpenFlyout((prev) => (prev === 'history' ? null : 'history'))}
          >
            <span className="rail-btn-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                <line x1="8" y1="8" x2="16" y2="8" />
                <line x1="8" y1="12" x2="13" y2="12" />
              </svg>
            </span>
            <span className="rail-btn-label">History</span>
            {!expanded && <span className="rail-tooltip">History</span>}
          </button>

          {/* Primary rail modules promoted to top-level navigation for archetype / liquid workspaces.
              These appear right after History (same visual tier as New chat / History) so the custom
              Writer's Studio items (Scratch, Chapter Work, etc.) feel like the main first-class rail. */}
          {primaryModules.length > 0 && (
            <>
              <div className="rail-writers-studio-header">
                Writer’s Studio
              </div>
              <div className="rail-primary-top-level">
                {primaryModules.map((m) => {
                  const railKey = `primary:${m.id}`;
                  const icon = getPrimaryModuleIcon(m.type, m.label, m.config as Record<string, unknown> | undefined);
                  return (
                    <RailItem
                      key={railKey}
                      railKey={railKey}
                      label={m.label}
                      activeKey={activeRailKey}
                      expanded={expanded}
                      onClick={handleRailClick}
                      onHover={handleRailHover}
                      count={0}
                      isGroupOpen={false}
                      subItems={[]}
                      onSubItemClick={undefined}
                    >
                      {icon}
                    </RailItem>
                  );
                })}
              </div>
            </>
          )}

          {/* Divider before modules */}
          <div className="rail-divider" />

          {/* MODULES section header — fixed OUTSIDE the scroll container (a
              flex-shrink:0 sibling) so it and its settings button stay put while
              only the module list below scrolls. */}
          <div
            className="rail-section-header rail-modules-header"
            onClick={undefined}
          >
            <span className="rail-section-text">{isTeamWorkspace ? <>PER<span className="rail-section-text-rest">SONAL</span></> : <>MOD<span className="rail-section-text-rest">ULES</span></>}</span>
            <button
              className={`rail-section-add${managerSection === 'individual' ? ' active' : ''}`}
              onClick={toggleManager}
              title="Module visibility"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />
              </svg>
            </button>
          </div>

          {/* Scrollable modules section — only the items scroll; the header above is fixed. */}
          <div ref={railScrollRef} className={railModulesScrollClassName(isManagerOpen)}>
            {isManagerOpen ? (
              // ── Manage mode ──
              // In team workspaces: two static sections (Individual / Team) so
              // each member can toggle personal and shared modules independently.
              // In personal workspaces: one unified draggable list (unchanged).
              isTeamWorkspace ? (() => {
                const teamEntries = sortRailItemsByOrder(
                  manageEntries.filter((e) => isSharedTeamRailKey(e.key)),
                  TEAM_RAIL_ORDER,
                ).map(toTeamLabel);
                const renderManageRow = (entry: ManageEntry) => (
                  <RailItem
                    key={entry.key}
                    railKey={entry.key}
                    label={entry.label}
                    activeKey={entry.isOn ? activeRailKey : null}
                    expanded={expanded}
                    onClick={entry.isOn ? handleRailClick : () => {}}
                    onHover={entry.isOn ? handleRailHover : undefined}
                    dimmed={!entry.isOn}
                    toggle={{
                      on: entry.isOn,
                      onToggle: () => handleVisibilityToggle(entry.visKey, !entry.isOn),
                    }}
                    shake={entry.key === shakeKey}
                  >
                    {entry.icon}
                  </RailItem>
                );
                // 'team' mode: normal provider items at top + team toggles below
                if (managerSection === 'team') {
                  const renderNormalItem = (item: OnRailItem) => (
                    <RailItem
                      key={item.key}
                      railKey={item.key}
                      label={item.label}
                      activeKey={activeRailKey}
                      expanded={expanded}
                      onClick={handleRailClick}
                      onHover={handleRailHover}
                      badge={item.badge}
                      count={item.count}
                      isGroupOpen={expandedGroups.has(item.key)}
                      subItems={item.subItems}
                      onSubItemClick={handleSubItemClick}
                    >
                      {item.icon}
                    </RailItem>
                  );
                  return (
                    <>
                      <div className="rail-reorder-group">
                        {individualRailItems.map(renderNormalItem)}
                      </div>
                      <div className="rail-section-header rail-team-modules-header">
                        <span className="rail-section-text">TEA<span className="rail-section-text-rest">M</span></span>
                        <button
                          className="rail-section-add active"
                          onClick={toggleTeamManager}
                          title="Team module visibility"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />
                          </svg>
                        </button>
                      </div>
                      <div className="rail-reorder-group">
                        {teamEntries.map(renderManageRow)}
                      </div>
                    </>
                  );
                }
                // 'individual' mode: email/calendar/drive toggles + normal team section below
                // Individual (per-member) toggles: provider modules + automations.
                const providerEntries = manageEntries.filter((e) => isIndividualTeamRailKey(e.key));
                const renderNormalItem = (item: OnRailItem) => (
                  <RailItem
                    key={item.key}
                    railKey={item.key}
                    label={item.label}
                    activeKey={activeRailKey}
                    expanded={expanded}
                    onClick={handleRailClick}
                    onHover={handleRailHover}
                    badge={item.badge}
                    count={item.count}
                    isGroupOpen={expandedGroups.has(item.key)}
                    subItems={item.subItems}
                    onSubItemClick={handleSubItemClick}
                  >
                    {item.icon}
                  </RailItem>
                );
                return (
                  <>
                    <div className="rail-reorder-group">
                      {providerEntries.map(renderManageRow)}
                    </div>
                    <div className="rail-section-header rail-team-modules-header">
                      <span className="rail-section-text">TEA<span className="rail-section-text-rest">M</span></span>
                      <button
                        className="rail-section-add"
                        onClick={toggleTeamManager}
                        title="Team module visibility"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />
                        </svg>
                      </button>
                    </div>
                    <div className="rail-reorder-group">
                      {sharedTeamRailItems.map((item) => renderNormalItem(toTeamLabel(item)))}
                    </div>
                  </>
                );
              })() : (
              // Personal workspace: one unified, ordered, draggable list (on + off).
              // On and off rows share ONE Reorder.Group, each keyed by its stable
              // rail key. Toggling a module flips `isOn` and re-sorts the list, so
              // the SAME element stays mounted and Framer's layout animation slides
              // it to its new spot while CSS fades the dim — no vanish/pop between lists.
              <Reorder.Group
                ref={railReorderGroupRef}
                axis="y"
                as="div"
                className="rail-reorder-group"
                values={manageEntries.map((e) => e.key)}
                onReorder={(keys) => handleManageReorder(keys as string[])}
              >
                {manageEntries.map((entry) => (
                  <RailItem
                    key={entry.key}
                    railKey={entry.key}
                    label={entry.label}
                    activeKey={entry.isOn ? activeRailKey : null}
                    expanded={expanded}
                    onClick={entry.isOn ? handleRailClick : () => {}}
                    onHover={entry.isOn ? handleRailHover : undefined}
                    dimmed={!entry.isOn}
                    toggle={{
                      on: entry.isOn,
                      onToggle: () => handleVisibilityToggle(entry.visKey, !entry.isOn),
                    }}
                    reorderValue={entry.key}
                    draggable={entry.draggable}
                    shake={entry.key === shakeKey}
                    onReorderEnd={handleRailReorderEnd}
                    dragConstraints={railReorderGroupRef}
                  >
                    {entry.icon}
                  </RailItem>
                ))}
              </Reorder.Group>)
            ) : (
              (() => {
                // One renderer for every on-row (core + plugin) — same shape,
                // same order, so plugins behave exactly like core modules.
                const renderOnRailItem = (item: OnRailItem) => (
                  <RailItem
                    key={item.key}
                    railKey={item.key}
                    label={item.label}
                    activeKey={activeRailKey}
                    expanded={expanded}
                    onClick={handleRailClick}
                    onHover={handleRailHover}
                    badge={item.badge}
                    count={item.count}
                    isGroupOpen={expandedGroups.has(item.key)}
                    subItems={item.subItems}
                    onSubItemClick={handleSubItemClick}
                  >
                    {item.icon}
                  </RailItem>
                );

                // Channels item: chevron toggle + inline group-channel list.
                const renderChannelsOnRailItem = (item: OnRailItem) => {
                  const channelSubItems = railChannels
                    ? railChannels.map((ch) => ({ id: `ch:${ch.id}`, label: `# ${ch.name}`, count: ch.unreadCount }))
                    : [];
                  return (
                    <RailItem
                      key={item.key}
                      railKey={item.key}
                      label={item.label}
                      activeKey={activeRailKey}
                      expanded={expanded}
                      onClick={handleRailClick}
                      onHover={handleRailHover}
                      badge={item.badge}
                      isGroupOpen={true}
                      subItems={channelSubItems}
                      onSubItemClick={handleChannelSubItemClick}
                      alwaysOpen={true}
                    >
                      {item.icon}
                    </RailItem>
                  );
                };

                // DMs item: chevron toggle + inline member list.
                const renderDmsOnRailItem = (item: OnRailItem) => {
                  const currentUserId = profile?.id ?? '';
                  const unreadByUser = dmUnreadByUserId(railDms, currentUserId);
                  // Stay blank until BOTH the roster and the DM unread list have
                  // loaded — otherwise the list flashes members with all-zero
                  // badges before railDms arrives (old Promise.all was all-or-
                  // nothing; #4894).
                  const dmSubItems = spaceMembers && railDms
                    ? spaceMembers
                        .filter((m) => m.id !== currentUserId)
                        .map((m) => ({ id: m.id, label: m.name || m.email, count: unreadByUser[m.id] }))
                    : [];
                  return (
                    <RailItem
                      key={item.key}
                      railKey={item.key}
                      label={item.label}
                      activeKey={activeRailKey}
                      expanded={expanded}
                      onClick={handleRailClick}
                      onHover={handleRailHover}
                      badge={item.badge}
                      isGroupOpen={true}
                      subItems={dmSubItems}
                      onSubItemClick={handleDmSubItemClick}
                      alwaysOpen={true}
                    >
                      {item.icon}
                    </RailItem>
                  );
                };

                // Team workspace: provider modules (Email/Calendar/Drive) up top,
                // the rest (incl. plugins) under a "Team Modules" header.
                if (isTeamWorkspace) {
                  return (
                    <>
                      {individualRailItems.map(renderOnRailItem)}
                      {(teamRailItems.length > 0 || isTeamWorkspace) && (
                        <div className="rail-section-header rail-team-modules-header">
                          <span className="rail-section-text">TEA<span className="rail-section-text-rest">M</span></span>
                          <button
                            className={`rail-section-add${managerSection === 'team' ? ' active' : ''}`}
                            onClick={toggleTeamManager}
                            title="Team module visibility"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />
                            </svg>
                          </button>
                        </div>
                      )}
                      {sharedTeamRailItems.map((item) => {
                        const ti = toTeamLabel(item);
                        return ti.key === 'channels' ? renderChannelsOnRailItem(ti) :
                          ti.key === 'dms' ? renderDmsOnRailItem(ti) :
                          renderOnRailItem(ti);
                      })}
                    </>
                  );
                }
                // Solo workspace: single flat list (core + plugins, in order).
                return <>{onRailItems.map((item) =>
                  item.key === 'channels' ? renderChannelsOnRailItem(item) :
                  item.key === 'dms' ? renderDmsOnRailItem(item) :
                  renderOnRailItem(item)
                )}</>;
              })()
            )}
          </div>

          {/* Avatar — clicking opens Settings (WorkspaceOptionsModal) */}
          <div className="rail-avatar-wrap" onClick={() => { setOpenFlyout(null); onOpenWorkspaceOptions?.(); }}>
            <span className="rail-avatar-slot">
              <button className="rail-avatar" title={displayName}>
                {initials}
                {!expanded && <span className="rail-tooltip">{displayName}</span>}
              </button>
            </span>
            <span className="rail-avatar-name">{displayName}</span>
          </div>
        </div>

      </aside>

      {/* Chat history flyout */}
      {openFlyout === 'history' && (
        <ChatHistoryFlyout
          sidebarExpanded={expanded}
          conversations={allConversations}
          activeConversationId={activeConversation?.id}
          workspaceId={workspaceId}
          onSelect={(conv) => {
            setOpenFlyout(null);
            // Pure content swap: preserve the current chat-only / module+chat view
            // by encoding it in the URL, so MainLayout's default-module auto-open
            // effect doesn't force a module open. `view: 'chat'` suppresses it for
            // chat-only; a moduleId keeps module+chat on the SAME module. We
            // preserve the live open module id directly (the flyout's conversation
            // objects don't carry `.modules`, so we can't and needn't validate it
            // against the target — MainLayout resolves a missing one).
            const targetModule = isModulePaneOpen ? (currentModuleId ?? null) : null;
            navigate(
              buildConversationPath(
                workspaceId,
                conv.id,
                targetModule ? { moduleId: targetModule } : { view: 'chat' },
              ),
            );
          }}
          onClearAll={async () => {
            if (!token || !workspaceId) return;
            if (!(await confirmDialog({ message: 'Delete all conversations? This cannot be undone.', confirmLabel: 'Delete all', danger: true }))) return;
            try {
              await Promise.all(
                allConversations.map((c) =>
                  api.workspaces.deleteConversation(token, workspaceId, c.id)
                )
              );
              setOpenFlyout(null);
              onNewChat();
            } catch (err) {
              console.warn('[SidebarRail] Failed to clear conversations', err);
            }
          }}
          onClose={closeFlyout}
        />
      )}

      {/* Workspace switcher flyout (collapsed sidebar) */}
      {openFlyout === 'workspace' && (
        <>
          <div className="flyout-backdrop" onClick={closeFlyout} />
          <div className="notebooks-flyout open" style={{ left: `${expanded ? 220 : 48}px` }}>
            <div className="flyout-header">
              <span className="flyout-title">WORKSPACE</span>
            </div>
            <div className="flyout-list xv-scroll">
              {containers.map((container, index) => {
                const isSelected = container.id
                  ? selectedContainer?.id === container.id
                  : selectedContainer?.ownerId === container.ownerId;
                return (
                  <button
                    key={getContainerKey(container, index)}
                    className={`flyout-item${isSelected ? ' active' : ''}`}
                    onClick={() => {
                      if (!isSelected) void selectContainer(container.id || container.ownerId);
                      setOpenFlyout(null);
                    }}
                  >
                    <span style={{ width: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {isSelected && (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17L4 12" />
                        </svg>
                      )}
                    </span>
                    <span className="flyout-item-name" style={{ flex: 1 }}>{getContainerDisplayName(container)}</span>
                    {typeof container.memberCount === 'number' && (
                      <span style={{ fontSize: 11, color: 'var(--color-text-faint, #aaa)', flexShrink: 0, marginLeft: 8 }}>
                        {container.memberCount} {container.memberCount === 1 ? 'member' : 'members'}
                      </span>
                    )}
                  </button>
                );
              })}
              <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 4, paddingTop: 4 }}>
                <button
                  className="flyout-item"
                  onClick={() => {
                    setOpenFlyout(null);
                    setIsAddWorkspaceModalOpen(true);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  <span className="flyout-item-name">New Workspace</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Generic module flyout — shown for rail items with 2+ modules */}
      {openFlyout && openFlyout !== 'history' && openFlyout !== 'workspace' && (() => {
        // Core rail key flyout
        if (modulesByRailKey[openFlyout]) {
          return (
            <ModuleFlyout
              title={(isTeamWorkspace ? RAIL_LABELS : PERSONAL_RAIL_LABELS)[openFlyout] ?? openFlyout.toUpperCase()}
              isOpen
              sidebarExpanded={expanded}
              modules={modulesByRailKey[openFlyout]}
              activeModuleId={effectiveModuleId}
              onSelect={(mod) => handleFlyoutSelect(openFlyout, mod)}
              onClose={closeFlyout}
            />
          );
        }
        // Plugin module flyout (key = "plugin:<type>")
        if (openFlyout.startsWith('plugin:')) {
          const type = openFlyout.slice(7);
          const modules = pluginModules[type];
          if (modules && modules.length >= 2) {
            const meta = PLUGIN_MODULE_META[type];
            return (
              <ModuleFlyout
                title={(meta?.label ?? type).toUpperCase()}
                isOpen
                sidebarExpanded={expanded}
                modules={modules}
                activeModuleId={effectiveModuleId}
                onSelect={(mod) => handleFlyoutSelect(openFlyout, mod)}
                onClose={closeFlyout}
              />
            );
          }
        }
        return null;
      })()}

      <PlanChoiceModal
        isOpen={isAddWorkspaceModalOpen}
        onClose={() => setIsAddWorkspaceModalOpen(false)}
        onCreated={async (spaceId) => {
          await fetchContainers();
          await selectContainer(spaceId);
        }}
      />
    </>
  );
}
